import { useCallback } from 'react';
import { SummaryPeriod } from './useMultiPeriodFocusStats';

interface SummaryFingerprint {
  completedTasks: number;
  totalTasks: number;
  focusMinutes: number;
  sessionCount: number;
}

interface CachedSummary {
  summary: string;
  fingerprint: SummaryFingerprint;
  generatedAt: string;
  periodLabel: string;
}

const CACHE_KEY_PREFIX = 'ai_summary_';

export function useAISummaryCache() {
  const getCacheKey = (period: SummaryPeriod): string => {
    return `${CACHE_KEY_PREFIX}${period}`;
  };

  const createFingerprint = (
    tasks: { completed?: boolean }[],
    sessions: { duration_minutes?: number }[]
  ): SummaryFingerprint => {
    const completedTasks = tasks.filter((t) => t.completed).length;
    const totalTasks = tasks.length;
    const focusMinutes = sessions.reduce(
      (sum, s) => sum + (s.duration_minutes || 0),
      0
    );
    const sessionCount = sessions.length;

    return { completedTasks, totalTasks, focusMinutes, sessionCount };
  };

  const fingerprintsMatch = (
    a: SummaryFingerprint,
    b: SummaryFingerprint
  ): boolean => {
    return (
      a.completedTasks === b.completedTasks &&
      a.totalTasks === b.totalTasks &&
      a.focusMinutes === b.focusMinutes &&
      a.sessionCount === b.sessionCount
    );
  };

  const getCachedSummary = useCallback(
    (period: SummaryPeriod): CachedSummary | null => {
      try {
        const key = getCacheKey(period);
        const cached = localStorage.getItem(key);
        if (!cached) return null;
        return JSON.parse(cached) as CachedSummary;
      } catch {
        return null;
      }
    },
    []
  );

  const setCachedSummary = useCallback(
    (
      period: SummaryPeriod,
      summary: string,
      fingerprint: SummaryFingerprint,
      periodLabel: string
    ): void => {
      try {
        const key = getCacheKey(period);
        const cached: CachedSummary = {
          summary,
          fingerprint,
          generatedAt: new Date().toISOString(),
          periodLabel,
        };
        localStorage.setItem(key, JSON.stringify(cached));
      } catch (e) {
        console.error('Failed to cache summary:', e);
      }
    },
    []
  );

  const shouldRegenerateSummary = useCallback(
    (
      period: SummaryPeriod,
      currentTasks: { completed?: boolean }[],
      currentSessions: { duration_minutes?: number }[]
    ): { shouldRegenerate: boolean; cached: CachedSummary | null } => {
      const cached = getCachedSummary(period);
      if (!cached) {
        return { shouldRegenerate: true, cached: null };
      }

      const currentFingerprint = createFingerprint(currentTasks, currentSessions);
      const shouldRegenerate = !fingerprintsMatch(
        cached.fingerprint,
        currentFingerprint
      );

      return { shouldRegenerate, cached };
    },
    [getCachedSummary]
  );

  const getRelativeTime = useCallback((isoString: string): string => {
    const now = new Date();
    const then = new Date(isoString);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return then.toLocaleDateString();
  }, []);

  return {
    createFingerprint,
    getCachedSummary,
    setCachedSummary,
    shouldRegenerateSummary,
    getRelativeTime,
  };
}
